// =============================================================================
// SsoShopifyPage — Consume a one-time Shopify SSO token
// =============================================================================
// Landing page for the "Manage on Portal" flow. The Shopify embedded app
// mints a single-use JWT via the API and redirects the merchant here with
// `?token=...`. This page POSTs the token to `/api/v1/auth/sso/consume`,
// which validates + single-use enforces it and sets the normal auth
// cookies (__tk_access / __tk_refresh / __tk_csrf). On success we hydrate
// the auth store and navigate to /dashboard.
//
// Concurrency note:
//   AuthInitializer runs on every mount and calls GET /auth/me. That call
//   starts before our consume() sets the auth cookies, so it returns
//   `{ user: null }` and then (if it resolves after consume) overwrites
//   our freshly-authenticated state back to 'unauthenticated'. To avoid
//   this race we wait for the auth store status to leave the
//   'idle' / 'loading' range BEFORE calling consume.
//
// Security notes:
//   - The token is never written to localStorage; it lives only in the
//     URL query string and is stripped as soon as consume resolves.
//   - The API enforces TTL + single-use (jti in Redis); a reload or a
//     shared link cannot re-authenticate.
//   - On any error we route the user to /login and surface a friendly
//     message, without leaking the token.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { AuthUser } from '@tkpod/shared';
import { Button, Card, Spinner } from '@tkpod/ui';
import { api, startSessionKeeper } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

type PageState =
  | { status: 'waiting' }
  | { status: 'consuming' }
  | { status: 'error'; message: string };

/**
 * Match a same-origin absolute path starting with a single `/`. The
 * leading negative lookahead blocks protocol-relative URLs (`//evil`)
 * which browsers treat as cross-origin. Anything else — absolute URLs,
 * JavaScript URIs, etc. — fails the regex and falls back to /dashboard.
 * This is a client-side guard; the API enforces the same check server-
 * side before embedding `next` in the signed link.
 */
const SAFE_NEXT_PATH = /^\/(?!\/)[A-Za-z0-9._~!$&'()*+,;=:@%/?#-]*$/;

function sanitizeNext(raw: string | null): string {
  if (!raw) return '/dashboard';
  return SAFE_NEXT_PATH.test(raw) ? raw : '/dashboard';
}

export function SsoShopifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const consumedRef = useRef(false);
  const authStatus = useAuthStore((s) => s.status);
  const [state, setState] = useState<PageState>({ status: 'waiting' });

  useEffect(() => {
    // Re-entry / double-consume guard (StrictMode, HMR, navigation-back).
    if (consumedRef.current) return;

    // Wait for AuthInitializer (mounted at the app root) to finish its
    // /auth/me round-trip. Without this gate, initialize() can resolve
    // after consume() and clobber the authenticated state we just set.
    if (authStatus === 'idle' || authStatus === 'loading') return;

    // IMPORTANT: we deliberately do NOT short-circuit when the store
    // is already `authenticated`. The merchant may be signed in as a
    // different user (e.g. they clicked "Manage on Portal" from a shop
    // owned by account B while their Portal tab was authenticated as
    // A). Running consume unconditionally ensures the tab ends up
    // signed in as the identity embedded in the SSO token — matching
    // the "latest login wins" semantics of the regular /login flow.
    // The backend overwrites the auth cookies and revokes the previous
    // refresh token atomically.

    const token = searchParams.get('token');
    if (!token) {
      consumedRef.current = true;
      setState({
        status: 'error',
        message: 'This sign-in link is missing a token.',
      });
      return;
    }

    // Read `next` BEFORE stripping the query string below. Only same-
    // origin absolute paths are honored; everything else falls back to
    // `/dashboard`. The API validates the same value server-side so a
    // crafted link cannot request a cross-origin redirect.
    const nextTarget = sanitizeNext(searchParams.get('next'));

    consumedRef.current = true;
    setState({ status: 'consuming' });

    // Strip the token from the URL BEFORE making the consume call. This
    // prevents the Referer header (and any third-party script reading
    // document.URL) from observing the token during the API round-trip.
    // Modern browsers' default Referrer-Policy already strips path/query
    // on cross-origin requests, but this is a belt-and-braces safeguard
    // that also clears the address bar and browser history entry.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      // replaceState may throw in some sandboxed contexts — not fatal.
    }

    void (async () => {
      try {
        const response = await api.post<{ user: AuthUser }>(
          '/api/v1/auth/sso/consume',
          { token },
          { noRetry: true },
        );

        useAuthStore.getState().setUser(response.user);
        startSessionKeeper();
        navigate(nextTarget, { replace: true });
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : 'Sign-in link is invalid or has expired.';
        setState({ status: 'error', message });
      }
    })();
  }, [authStatus, searchParams, navigate]);

  if (state.status !== 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-sm text-text-secondary">
            Signing you into Transfer Kingdom Portal…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <Card className="max-w-md w-full">
        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Sign-in link unavailable
          </h2>
          <p className="text-sm text-text-secondary mb-6">{state.message}</p>
          <Link to="/login">
            <Button variant="outline">Go to Login</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
